import { useCallback, useState } from 'react'
import type { Communication, Practitioner } from '@medplum/fhirtypes'
import { useMedplum, useMedplumProfile } from '@medplum/react'
import {
  createThreadMessage,
  type MessageThreadSummary,
  type PractitionerThreadOption,
  type ThreadMessage,
} from './service'

/** A message the user has submitted that is not yet confirmed by the server. */
export type PendingMessage = {
  localId: string
  text: string
  status: 'sending' | 'failed'
  /** Local clock, used only for ordering the optimistic bubble at the end. */
  createdAt: string
}

type UseThreadMessageSenderArgs = {
  threadSummary?: MessageThreadSummary
  onCreated?: (message: ThreadMessage) => void
}

export type ThreadMessageSender = {
  pending: PendingMessage[]
  /** Submit new text. Resolves false when the send failed. */
  send: (text: string) => Promise<boolean>
  retry: (localId: string) => void
  /**
   * Drop the optimistic bubble once its real message shows up.
   *
   * `ThreadPane.tsx` documents why this matters: the `part-of` subscription can
   * deliver the sender's own message while `createThreadMessage` is still
   * awaiting `updateThreadReadState`. Matching on text lets the optimistic entry
   * retire on whichever arrives first, so the bubble never double-renders.
   */
  reconcile: (message: Communication, senderId: string | undefined) => void
}

export function useThreadMessageSender({
  threadSummary,
  onCreated,
}: UseThreadMessageSenderArgs): ThreadMessageSender {
  const medplum = useMedplum()
  const profile = useMedplumProfile()
  const practitionerProfile = profile?.resourceType === 'Practitioner' ? profile : undefined
  const [pending, setPending] = useState<PendingMessage[]>([])

  const submit = useCallback(
    async (localId: string, text: string): Promise<boolean> => {
      if (!threadSummary || !practitionerProfile?.id) {
        return false
      }

      const recipients = [threadSummary.sender, ...threadSummary.recipients]
        .filter((participant): participant is PractitionerThreadOption =>
          Boolean(
            participant?.practitioner?.id && participant.practitioner.id !== practitionerProfile.id,
          ),
        )
        .map((participant): Practitioner => participant.practitioner)

      if (recipients.length === 0) {
        return false
      }

      setPending((prev) =>
        prev.some((item) => item.localId === localId)
          ? prev.map((item) => (item.localId === localId ? { ...item, status: 'sending' } : item))
          : [...prev, { localId, text, status: 'sending', createdAt: new Date().toISOString() }],
      )

      try {
        const created = await createThreadMessage({
          thread: threadSummary.thread,
          sender: practitionerProfile,
          recipients,
          message: text,
          medplum,
        })

        setPending((prev) => prev.filter((item) => item.localId !== localId))
        onCreated?.({ message: created, sender: practitionerProfile, recipients })
        return true
      } catch {
        // Surfaced as a Failed bubble with Retry rather than a toast, so the
        // message the user typed is never lost.
        //
        // Re-add rather than only patching in place: `reconcile` correlates by
        // text, so sending the same text twice at once can retire THIS entry
        // against the other send's subscription event. A plain `map` would then
        // be a silent no-op and a genuinely failed send would show no Failed
        // state and no Retry. The request's own outcome is authoritative.
        setPending((prev) =>
          prev.some((item) => item.localId === localId)
            ? prev.map((item) => (item.localId === localId ? { ...item, status: 'failed' } : item))
            : [...prev, { localId, text, status: 'failed', createdAt: new Date().toISOString() }],
        )
        return false
      }
    },
    [medplum, onCreated, practitionerProfile, threadSummary],
  )

  const send = useCallback((text: string) => submit(crypto.randomUUID(), text), [submit])

  const retry = useCallback(
    (localId: string) => {
      const target = pending.find((item) => item.localId === localId)
      if (target) {
        void submit(localId, target.text)
      }
    },
    [pending, submit],
  )

  const reconcile = useCallback(
    (message: Communication, senderId: string | undefined) => {
      if (!practitionerProfile?.id || senderId !== practitionerProfile.id) {
        return
      }

      const text = message.payload?.[0]?.contentString
      if (!text) {
        return
      }

      setPending((prev) => {
        const index = prev.findIndex((item) => item.status === 'sending' && item.text === text)
        return index === -1 ? prev : prev.filter((_, position) => position !== index)
      })
    },
    [practitionerProfile?.id],
  )

  return { pending, send, retry, reconcile }
}
